import util from "util";
import Promise from "bluebird";
import moment from "moment";
import api from "../../axios_api";
import _ from "lodash";
import { ADDRESS_FETCH } from "../action_types";
import { vals } from "../../util";
import router from "../../main";

const stringify = JSON.stringify;
const READ_INTERVAL = 10; // cycle counts (10 cycles per read ~10s)
const WRITE_INTERVAL = 5; // cycle counts (5 cycles per write ~5s)
const COMPANY_HEADERS = ["sector", "geography", "stage", "status", "rating"];

function checkDefaults(data) {
  let dkeys = _.keys(vals.PORTFOLIO_DEFAULTS);
  return _.map(data, gram => {
    // default values for specific columns
    return _.forIn(gram, (v, k, o) => {
      let invalid =
        _.isNil(v) ||
        (typeof v === "string" && _.isEmpty(v)) ||
        (typeof v === "number" && _.isNaN(v));
      if (_.indexOf(dkeys, k) >= 0 && invalid) {
        delete o[k];
        o[k] = vals.PORTFOLIO_DEFAULTS[k];
      }
      return o[k];
    });
  });
}

/**
 * Portfolio {
 * profile: user information (empty)
 * company: company information (filled)
 * ownership: % owned (number)
 * cost: $ cost (number)
 * value: $ value (number)
 * initial: initial investment (date)
 * direct: direct/lp
 * modified: last modified timestamp (date, empty)
 * context: context in portfolio (invest, watch, mentor)
 * }
 */

const state = {
  server_data: [], // data as it is on our server
  local_cache: [], // local changes yet to be committed to the server
  has_data: false,
  version: 0, // incrementing number that indicates that server_data (or local cache) was modified
  _write_timer: 0,
  _force_fetch: false,
  _read_timer: 0,
  workspace_id: "",
  account_id: "",
  _processing_cycle: false,
  loaded: false,
  updationUserCheck: 0 // necessary to make sure we aren't doing multiple reads/writes at the same time (single thread for now)
};

const getters = {
  dataLoaded: function(state, getters, rootState, rootGetters) {
    return state.loaded;
  },
  portfolioAll: function(state, getters, rootState, rootGetters) {
    let retval = state.server_data;
    console.log("total", state.server_data);
    return _.filter(retval, function(obj) {
      return (
        obj &&
        obj.direct == "Direct" &&
        ((obj.context && obj.context == "invest") || _.isNil(obj.context)) &&
        obj.user_investment_status != "Fund" &&
        obj.data_status != "NoCompanyUrl"
      );
    });
  },
  fund: function(state, getters, rootState, rootGetters) {
    let data = state.server_data;
    let retval = {};
    let index = data.findIndex(item => {
      if (item && item.user_investment_status == "Fund") {
        return item;
      }
    });
    if (index >= 0) {
      retval = JSON.parse(JSON.stringify(data[index]));
    }
    return retval;
  },
  portfolioAddCompany: function(state, getters, rootState, rootGetters) {
    let retval = state.server_data;
    return _.filter(retval, function(obj) {
      return obj && obj.direct == "Direct";
    });
  },
  portfolioData: function(state, getters, rootState, rootGetters) {
    let retval = state.server_data;
    let data = JSON.parse(JSON.stringify(retval));
    retval.map(item => {
      if (item.initial_investment && item.initial_investment != "-") {
        item.initial_investment = moment(item.initial_investment).format(
          "MM/DD/YYYY"
        );
      }
      if (item.last_funding_round && item.last_funding_round != "-") {
        item.last_funding_round = moment(item.last_funding_round).format(
          "MM/DD/YYYY"
        );
      }
    });
    return retval.filter(
      item =>
        item.user_investment_status == "Invested" &&
        item.context == "invest" &&
        item.direct == "Direct"
    );
    //return retval;
  },
  getPortfolio: state => id => {
    // return the portfolio data with mongo id
    let retval = _.reduce(
      state.server_data,
      (m, gram) => {
        if (gram._id === id) return gram;
        return m;
      },
      {}
    );
    // get any local modifications
    let local = _.reduce(
      state.local_cache,
      (m, gram) => {
        if (gram._id === id) return gram;
        return m;
      },
      {}
    );
    return _.assignIn({}, retval, local);
  },
  hasData: function(state) {
    return state.has_data || state.server_data.length > 0;
  },
  isDirty: function(state) {
    return state.local_cache.length > 0;
  },
  portfolioValue: function(state, getters) {
    // return value of portfolio
    return _.reduce(
      getters["portfolioData"],
      (memo, data) => {
        return memo + data.value;
      },
      0
    );
  },
  portfolioCost: function(state, getters) {
    return _.reduce(
      getters["portfolioData"],
      (memo, data) => {
        return memo + data.cost;
      },
      0
    );
  },
  portfolioGain: function(state, getters) {
    return getters["portfolioValue"] - getters["portfolioCost"];
  },
  portfolioCount: function(state) {
    return state.server_data.length;
  },
  version: function(state) {
    return state.version;
  },
  getPortfolioRole(state, dispatch) {
    return dispatch("apiFetchPortfolio");
  }
};

const mutations = {
  initialize(state) {
    // revert the portfolio back to '0'
    state.server_data = [];
    state.local_cache = [];
    state.has_data = false;
    state._write_timer = 0;
    state._force_fetch = false;
    state._read_timer = 0;
    state.version = 0;
    state._processing_cycle = false;
    state.updationUserCheck = 0;
    state.loaded = false;
  },
  setWorkspaceId(state, payload) {
    state.workspace_id = payload;
  },
  setAccountId(state, payload) {
    state.account_id = payload;
  },
  markData(state, payload) {
    state.has_data = payload.hasData || state.server_data.length > 0;
  },
  overwrite(state, payload) {
    // stomp on our current data, this will wipe out local changes
    let formatData = checkDefaults(payload); // make sure we have correct default values
    state.server_data = formatData;
    // stomp local changes
    state.local_cache = [];
    state.version++;
  },
  clean(state, payload) {
    // payload is data written to the server
    // remove any data older than _sent from the local cache
    state.local_cache = _.without(
      _.map(state.local_cache, cached => {
        let item = _.find(payload, function(p) {
          return p._id == cached._id;
        });
        if (!item || item._sent < cached._dirty) return cached; // return only if dont have it in the payload or it is newer
      }),
      undefined,
      null
    );
  },
  merge(state, payload) {
    //  if (_.isNil(payload) || _.isEmpty(payload)) return
    // merge what was returned from the server with what we have in server
    state.server_data = _.without(
      _.map(state.server_data, data => {
        if (!data) return data;
        let index = _.findIndex(payload, function(p) {
          return p._id == data._id;
        });
        if (index >= 0) {
          let item = _.first(_.pullAt(payload, index));
          if (_.lte(item.__v, data.__v)) return data;
          return _.omit(_.merge(data, item), ["_sent", "_dirty"]);
        }
        return data;
      }),
      null,
      undefined
    );
    // add remaining updates
    state.server_data = _.concat(state.server_data, payload);
    state.version++;
  },
  update(state, payload) {
    // add payload data to our local cache
    if (!_.isArray(payload)) payload = [payload];
    // payload = _.map(payload, item => {
    //   item = _.omitBy(item, function (v, k) { return _.indexOf(COMPANY_HEADERS, k) >= 0 })
    //   if (_.keys(item).length < 2) return null
    //   return item
    // })
    payload = _.without(payload, null, undefined);
    if (payload.length < 1) return; // remove any updates that are not pertinent to this collection
    // get our _id for the company
    let update = _.without(
      _.map(payload, item => {
        if (_.isNil(item.name) || _.isEmpty(item.name)) return null;
        let company = _.find(state.server_data, function(d) {
          return d.name.toLowerCase() == item.name.toLowerCase();
        });
        if (company)
          return _.assign({}, item, { _id: company._id, __v: company.__v });
      }),
      undefined,
      null
    );
    // remove any company headers
    // update = _.map(update, item => {
    //   return _.omitBy(item, function (v, k) { return _.indexOf(COMPANY_HEADERS, k) >= 0 })
    // })
    if (!_.isEmpty(update)) {
      state.local_cache = _.map(state.local_cache, cached => {
        let index = _.findIndex(update, function(u) {
          return u._id == cached._id;
        });
        if (index >= 0) {
          let cpny = _.first(_.pullAt(update, index));
          return _.merge(cached, cpny, { _dirty: moment() });
        }
        return cached;
      });
      // add remaining updates, that were not merged
      state.local_cache = _.concat(state.local_cache, update);
    }
    state.version++;
  },
  remove(state, company_id) {
    state.server_data = state.server_data.filter(
      item => item.ucid == company_id || item._id == company_id
    );
    // if (_.isNil(company_id) || _.isEmpty(company_id)) return
    // // remove the portfolio from server and local with the company id
    // state.server_data = _.map(state.server_data, gram => {
    //   if (gram.company && gram.company == company_id) return null
    //   return gram
    // })
    // state.server_data = _.without(state.server_data, null, undefined)

    // state.local_cache = _.map(state.local_cache, gram => {
    //   if (gram.company && gram.company == company_id) return null
    //   return gram
    // })
    // state.local_cache = _.without(state.local_cache, null, undefined)
    state.version++;
  }
};

const actions = {
  start({ state, dispatch, commit }) {
    state._write_timer = 0;
    state._force_fetch = true;
    state._processing_cycle = false;
    // force a get at first
    commit("initialize");
    dispatch("apiFetchPortfolio")
      .then(() => {
        state._processing_cycle = false;
      })
      .catch(err => {
        console.error(`Error reading: ${err}`);
        state._processing_cycle = false;
      });
    dispatch("forceRead");
    dispatch("cycle");
  },
  stop({ state, commit }) {
    // clean up
    commit("initialize");
    state._processing_cycle = false;
  },
  cycle({ state, getters, dispatch, rootGetters }) {
    state._write_timer += 1;
    state._read_timer += 1;
    if (!state._processing_cycle && rootGetters["user/hasToken"]) {
      state._processing_cycle = true;
      // check to see if we need to do any writes

      if (getters["isDirty"] && state._write_timer >= WRITE_INTERVAL) {
        state._write_timer = 0;
        // write to our server
        return dispatch("apiPatchPortfolio")
          .then(fail => {
            // do a fetch/merge if we have any fails or if we are passed the timer
            if (fail.length > 0) dispatch("forceRead");
            state._processing_cycle = false;
          })
          .catch(err => {
            console.error(`Error writting: ${err}`);
            state._processing_cycle = false;
          });
      } else if (state._read_timer >= READ_INTERVAL) {
        state._read_timer = 0;
        // get update from our server
        return dispatch("apiFetchPortfolio")
          .then(() => {
            state._processing_cycle = false;
          })
          .catch(err => {
            console.error(`Error reading: ${err}`);
            state._processing_cycle = false;
          });
      } else {
        state._processing_cycle = false; // not doing anything
      }
    }
  },
  forceRead({ state }) {
    state._read_timer = READ_INTERVAL;
  },
  forceWrite({ state }) {
    state._write_timer = WRITE_INTERVAL;
  },
  validateCompanyObject({}, payload) {
    if (_.isNil(payload) || _.isEmpty(payload))
      return Promise.reject({ errors: ["No company object provided"] });
    // go through payload and make sure the required fields are filled out, and that format of all fields is correct
  },
  apiPostPortfolioInst({ rootGetters }, payload) {
    // payload should be a company instance
    let currentRole = rootGetters["role/current_role"];
    let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/portfolio`;
    // if (currentRole && currentRole._id) {
    //   url = `/portfolio?account=${currentRole.account}`;
    //  } else  url = "/portfolio";
    return api
      .post(url, { encrypt: { data: payload } })
      .then(res => {
        // res should be the updated company info
        return Promise.resolve(res.data.encrypt);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  },
  apiPatchPortfolioInst({}, payload) {
    // payload should be a Company instance
    return api
      .patch("/portfolio", { encrypt: { data: payload } })
      .then(res => {
        return Promise.resolve(res.data.encrypt);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  },
  apiDeleteReferrers({}, payload) {
    // payload should be a Company instance
    return api
      .patch("/delete/referrers", { encrypt: { data: payload } })
      .then(res => {
        return Promise.resolve(res.data.encrypt);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  },
  apiDeletePortfolio({ dispatch, rootGetters }, payload) {
    // make sure we have a ucid and remove the portfolio for that company
    if (_.isNil(payload) || _.isEmpty(payload) || !payload.ucid || !payload._id)
      return Promise.reject({ error: "no company id" });
    let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/portfolio/${payload.ucid}`;
    //let currentRole = rootGetters['role/current_role'];
    // if (currentRole && currentRole._id) {
    //   url = `/portfolio?account=${currentRole.account}`;
    //  } else  url = "/portfolio";
    return api
      .delete(url)
      .then(res => {
        // assume success
        // commit('remove', payload._id)
        return dispatch("_apiFetchPortfolio");
      })
      .catch(err => {
        // failed to delete
        return Promise.reject({ error: err });
      });
  },
  apiPostPortfolio({ commit, dispatch }, payload) {
    if (!_.isArray(payload)) payload = [payload];
    // this is a stomp op
    return new Promise((resolve, reject) => {
      // this is assumed to be direct transfered, parsed data from xls
      // remove any columns we don't want
      // payload = _.map(payload, item => {
      //   return _.omitBy(item, function (v, k) { return vals.PORTFOLIO_HEADERS.indexOf(k.toUpperCase()) < 0 })
      // })
      let diligenceMentorArray = [];
      let otherArray = [];
      let finalArray = [];
      let withoutUrlArray = [];
      let withUrlArray = [];
      let finalObj = {};
      // fill in default context if one is not provided (can infer slightly from status)
      payload = _.map(payload, item => {
        let status = item.status || item.Status;
        status = status || "";
        let context =
          status && status.toUpperCase() === "DILIGENCE"
            ? vals.PORTFOLIO_CONTEXT.Watch
            : status.toUpperCase() === "MENTOR"
            ? vals.PORTFOLIO_CONTEXT.Mentor
            : vals.PORTFOLIO_CONTEXT.Invest;
        item.context = !_.isNil(item.context) ? item.context : context;
        if (
          status &&
          (status.toUpperCase() == "DILIGENCE" ||
            status.toUpperCase() == "MENTOR")
        ) {
          diligenceMentorArray.push(item);
        } else {
          otherArray.push(item);
        }
        return item;
      });
      for (let i = 0; i < otherArray.length; i++) {
        if (i > 0 && otherArray[i] && !otherArray[i].company_url) {
          if (otherArray[i].cost && !otherArray[i].name) {
            if (finalArray && finalArray.length > 0) {
              finalArray[finalArray.length - 1].hasOwnProperty("cost") &&
              finalArray[finalArray.length - 1].hasOwnProperty("amountArr")
                ? finalArray[finalArray.length - 1].amountArr.push(
                    otherArray[i].cost
                  )
                : (finalArray[finalArray.length - 1].amountArr = [
                    otherArray[i].cost
                  ]);
              finalArray[finalArray.length - 1].hasOwnProperty(
                "initial_investment"
              ) &&
              finalArray[finalArray.length - 1].hasOwnProperty(
                "initialInvestmentArr"
              )
                ? finalArray[finalArray.length - 1].initialInvestmentArr.push(
                    otherArray[i].initial_investment ||
                      moment(new Date()).format("MM/DD/YYYY")
                  )
                : (finalArray[finalArray.length - 1].initialInvestmentArr = [
                    otherArray[i].initial_investment ||
                      moment(new Date()).format("MM/DD/YYYY")
                  ]);
              finalArray[finalArray.length - 1].hasOwnProperty("ownership") &&
              finalArray[finalArray.length - 1].hasOwnProperty("ownershipArr")
                ? finalArray[finalArray.length - 1].ownershipArr.push(
                    otherArray[i].ownership
                  )
                : (finalArray[finalArray.length - 1].ownershipArr = [
                    otherArray[i].ownership
                  ]);
              finalArray[finalArray.length - 1].hasOwnProperty("value") &&
              finalArray[finalArray.length - 1].hasOwnProperty("valueArr")
                ? finalArray[finalArray.length - 1].valueArr.push(
                    otherArray[i].value
                  )
                : (finalArray[finalArray.length - 1].valueArr = [
                    otherArray[i].value
                  ]);
            }
          } else {
            if (otherArray[i].name) {
              if (finalArray && finalArray.length > 0) {
                let index = finalArray.findIndex(item => {
                  if (item.name && item.name == otherArray[i].name) {
                    return item;
                  }
                });
                if (index >= 0) {
                  finalArray[index].hasOwnProperty("cost") &&
                  finalArray[index].hasOwnProperty("amountArr")
                    ? finalArray[index].amountArr.push(otherArray[i].cost)
                    : (finalArray[index].amountArr = [otherArray[i].cost]);
                  finalArray[index].hasOwnProperty("initial_investment") &&
                  finalArray[index].hasOwnProperty("initialInvestmentArr")
                    ? finalArray[index].initialInvestmentArr.push(
                        otherArray[i].initial_investment ||
                          moment(new Date()).format("MM/DD/YYYY")
                      )
                    : (finalArray[index].initialInvestmentArr = [
                        otherArray[i].initial_investment ||
                          moment(new Date()).format("MM/DD/YYYY")
                      ]);
                  finalArray[index].hasOwnProperty("ownership") &&
                  finalArray[index].hasOwnProperty("ownershipArr")
                    ? finalArray[index].ownershipArr.push(
                        otherArray[i].ownership
                      )
                    : (finalArray[index].ownershipArr = [
                        otherArray[i].ownership
                      ]);
                  finalArray[finalArray.length - 1].hasOwnProperty("value") &&
                  finalArray[finalArray.length - 1].hasOwnProperty("valueArr")
                    ? finalArray[finalArray.length - 1].valueArr.push(
                        otherArray[i].value
                      )
                    : (finalArray[finalArray.length - 1].valueArr = [
                        otherArray[i].value
                      ]);
                } else {
                  if (index == -1) {
                    let obj = JSON.parse(JSON.stringify(otherArray[i]));
                    if (obj.cost) {
                      obj.amountArr = [obj.cost];
                    }
                    obj.initial_investment = obj.initial_investment
                      ? obj.initial_investment
                      : moment(new Date()).format("MM/DD/YYYY");
                    obj.initialInvestmentArr = obj.initial_investment
                      ? [obj.initial_investment]
                      : [moment(new Date()).format("MM/DD/YYYY")];
                    obj.ownershipArr = obj.ownership ? [obj.ownership] : [0];
                    obj.valueArr = obj.value ? [obj.value] : [0];
                    finalArray.push(obj);
                  }
                }
              } else {
                let obj = JSON.parse(JSON.stringify(otherArray[i]));
                if (obj.cost) {
                  obj.amountArr = [obj.cost];
                }
                obj.initial_investment = obj.initial_investment
                  ? obj.initial_investment
                  : moment(new Date()).format("MM/DD/YYYY");
                obj.initialInvestmentArr = obj.initial_investment
                  ? [obj.initial_investment]
                  : [moment(new Date()).format("MM/DD/YYYY")];
                obj.ownershipArr = obj.ownership ? [obj.ownership] : [0];
                obj.valueArr = obj.value ? [obj.value] : [0];
                finalArray.push(obj);
              }
            }
          }
        } else {
          let obj = JSON.parse(JSON.stringify(otherArray[i]));
          if (obj.cost) {
            obj.amountArr = [obj.cost];
          }
          obj.initial_investment = obj.initial_investment
            ? obj.initial_investment
            : moment(new Date()).format("MM/DD/YYYY");
          obj.initialInvestmentArr = obj.initial_investment
            ? [obj.initial_investment]
            : [moment(new Date()).format("MM/DD/YYYY")];
          obj.ownershipArr = obj.ownership ? [obj.ownership] : [0];
          obj.valueArr = obj.value ? [obj.value] : [0];
          finalArray.push(obj);
        }
      }
      // finalArray = finalArray.filter((item) => {
      //   if (!item.company_url && item.name) {
      //     item.direct = "Direct";
      //     item.data_status = "NoCompanyUrl";
      //     item.company_status = "Unknown";
      //     withoutUrlArray.push(item);
      //   }
      //   if (item.company_url) {
      //     return item;
      //   }
      // });
      let dupFinalArray = JSON.parse(JSON.stringify(finalArray));
      dupFinalArray.map((item, index) => {
        if (item) {
          if (item.name) {
            if (finalObj.hasOwnProperty(item.name)) {
              let obj = finalObj[item.name];
              if (
                item.hasOwnProperty("amountArr") &&
                item.amountArr.length > 0
              ) {
                obj.amountArr = obj.hasOwnProperty("amountArr")
                  ? [...obj.amountArr, ...item.amountArr]
                  : [...item.amountArr];
              }
              if (
                item.hasOwnProperty("initialInvestmentArr") &&
                item.initialInvestmentArr.length > 0
              ) {
                obj.initialInvestmentArr = obj.hasOwnProperty(
                  "initialInvestmentArr"
                )
                  ? [...obj.initialInvestmentArr, ...item.initialInvestmentArr]
                  : [...item.initialInvestmentArr];
              }
              if (
                item.hasOwnProperty("ownershipArr") &&
                item.ownershipArr.length > 0
              ) {
                obj.ownershipArr = obj.hasOwnProperty("ownershipArr")
                  ? [...obj.ownershipArr, ...item.ownershipArr]
                  : [...item.ownershipArr];
              }
              if (item.hasOwnProperty("valueArr") && item.valueArr.length > 0) {
                obj.valueArr = obj.hasOwnProperty("valueArr")
                  ? [...obj.valueArr, ...item.valueArr]
                  : [...item.valueArr];
              }
            } else {
              finalObj[item.name] = item;
            }
          } else {
            if (item.company_url) {
              if (finalObj.hasOwnProperty(item.company_url)) {
                let obj = finalObj[item.company_url];
                if (
                  item.hasOwnProperty("amountArr") &&
                  item.amountArr.length > 0
                ) {
                  obj.amountArr = obj.hasOwnProperty("amountArr")
                    ? [...obj.amountArr, ...item.amountArr]
                    : [...item.amountArr];
                }
                if (
                  item.hasOwnProperty("initialInvestmentArr") &&
                  item.initialInvestmentArr.length > 0
                ) {
                  obj.initialInvestmentArr = obj.hasOwnProperty(
                    "initialInvestmentArr"
                  )
                    ? [
                        ...obj.initialInvestmentArr,
                        ...item.initialInvestmentArr
                      ]
                    : [...item.initialInvestmentArr];
                }
                if (
                  item.hasOwnProperty("ownershipArr") &&
                  item.ownershipArr.length > 0
                ) {
                  obj.ownershipArr = obj.hasOwnProperty("ownershipArr")
                    ? [...obj.ownershipArr, ...item.ownershipArr]
                    : [...item.ownershipArr];
                }
                if (
                  item.hasOwnProperty("valueArr") &&
                  item.valueArr.length > 0
                ) {
                  obj.valueArr = obj.hasOwnProperty("valueArr")
                    ? [...obj.valueArr, ...item.valueArr]
                    : [...item.valueArr];
                }
              } else {
                finalObj[item.company_url] = item;
              }
            }
          }
        }
      });
      finalArray = Object.values(finalObj);
      finalArray.map(item => {
        if (
          item.valueArr &&
          item.valueArr.length &&
          item.initialInvestmentArr &&
          item.initialInvestmentArr.length
        ) {
          let initialInvestmentArr = JSON.parse(
            JSON.stringify(item.initialInvestmentArr)
          );
          initialInvestmentArr.sort((a, b) => {
            return new Date(b) - new Date(a);
          });
          let index = item.initialInvestmentArr.indexOf(
            initialInvestmentArr[0]
          );
          if (item.valueArr[index]) {
            item.value = item.valueArr[index];
          } else {
            item.value = item.valueArr[item.valueArr.length - 1];
          }
        } else {
          if (item.valueArr && item.valueArr.length) {
            item.value = item.valueArr[item.valueArr.length - 1];
          }
        }
        if (!item.company_url && item.name) {
          item.direct = "Direct";
          item.data_status = "NoCompanyUrl";
          item.company_status = "Unknown";
          withoutUrlArray.push(item);
        }
        if (item.company_url) {
          withUrlArray.push(item);
        }
      });
      let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/portfolio/portfolio/company`;
      //api.post(url, { 'encrypt': { data: payload } })
      // let excelDataArray = otherArray;
      // var i, j, chunckArray = [], chunk = 100;
      // for (i = 0, j = excelDataArray.length; i < j; i += chunk) {
      //   chunckArray.push(excelDataArray.slice(i, i + chunk));
      // }
      // let resolveCount = 0;
      // for (var k = 0; k < chunckArray.length; k++) {
      //   api.post(url, { 'encrypt': { data: chunckArray[k] } })
      //     .then(res => {
      //       resolveCount++;
      //       commit('markData', { hasData: true })
      //       if (resolveCount == chunckArray.length) {
      //         // do a get from the server (assume some data was written)
      //         dispatch('_apiFetchPortfolio')
      //         // this will return any validation errors received from the post
      //         resolve(res)
      //       }
      //     })
      //     .catch(err => {
      //       resolveCount++;
      //       reject(err)
      //     })
      // }
      api
        .post(url, { encrypt: { data: withUrlArray } })
        .then(async res => {
          let withoutUrlData = [];
          if (withoutUrlArray && withoutUrlArray.length > 0) {
            let withoutUrlApi = `/${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/excelUploadWithoutUrl`;
            withoutUrlData = await api
              .post(withoutUrlApi, { encrypt: { data: withoutUrlArray } })
              .catch(err => {
                reject(err);
              });
          }
          commit("markData", { hasData: true });
          // do a get from the server (assume some data was written)
          dispatch("_apiFetchPortfolio");
          // this will return any validation errors received from the post
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  apiPatchPortfolio({ state, commit }) {
    // take our current local cache and write it to server
    return new Promise((resolve, reject) => {
      let payload = _.map(state.local_cache, item => {
        // add a _sent item to the cache
        return _.omit(_.assign(item, { _sent: moment() }), [
          "_sent",
          "_dirty",
          "name"
        ]);
      });
      let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/portfolio`;
      api
        .patch(url, { encrypt: { data: payload } })
        .then(res => {
          // merge our successes
          commit("merge", _.clone(res.data.encrypt.success));
          // clean up our local cache
          commit("clean", _.clone(res.data.encrypt.success));
          // return fails
          resolve(res.data.encrypt.fail || []);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  apiFetchPortfolio({ state, commit, dispatch, rootGetters }) {
    if (
      router &&
      router.currentRoute &&
      router.currentRoute.params.account_id
    ) {
      return new Promise((resolve, reject) => {
        let params = {};
        if (state._force_fetch) {
          params.force = true;
          state._force_fetch = false;
        }
        let fiifi = JSON.parse(localStorage.fiifi).user;
        if (fiifi && fiifi.workspace_id && fiifi.workspace_id.length > 0) {
          let url = `${fiifi.id}/${fiifi.workspace_id}/portfolio`;
          return api
            .get(url, { params })
            .then(res => {
              // format the data from the server
              let companies = [];
              let formatted = _.map(res.data.encrypt.data, gram => {
                if (_.isObject(gram["company"])) {
                  gram["company_name"] = gram["company"]["name"];
                  gram["data_status"] = gram["company"]["data_status"];
                  gram["org_company_id"] = gram["company"]["_id"];
                  gram["deal_stage"] = gram["company"]["deal_stage"];
                  companies.push(_.merge({}, gram["company"])); // add company to collection we are going to send to our company data
                  delete gram["company"];
                }
                return gram;
              });
              // do any date-parsing
              formatted = _.map(formatted, gram => {
                return _.forIn(gram, (v, k, o) => {
                  if (vals.DATE_HEADERS.indexOf(k) >= 0) {
                    o[k] = moment.utc(v);
                  }
                });
              });
              // merge the data with our local info
              commit("overwrite", formatted);
              //commit('merge', formatted)
              commit("company/merge", { companies }, { root: true });
              //dispatch('_apiCheckCompanyAction', formatted);
              // fetch addresses for the companies
              // dispatch(`address/${ADDRESS_FETCH}`, {
              //   force: true, parents: _.reduce(companies, (memo, cpny) => {
              //     memo.push(cpny._id)
              //     return memo
              //   }, [])
              // }, { root: true })
              state.loaded = true;
              return resolve(formatted);
            })
            .catch(err => {
              return reject(err);
            });
        }
      });
    }
  },
  _apiFetchPortfolio({ state, commit, dispatch, rootGetters }) {
    return new Promise((resolve, reject) => {
      let fiifi = JSON.parse(localStorage.fiifi).user;
      if (fiifi && fiifi.workspace_id && fiifi.workspace_id.length > 0) {
        let url = `${fiifi.id}/${fiifi.workspace_id}/portfolio`;
        return api
          .get(url, { params: { force: true } })
          .then(res => {
            // format the data from the server
            let companies = [];
            let formatted = _.map(res.data.encrypt.data, gram => {
              if (_.isObject(gram["company"])) {
                gram["company_name"] = gram["company"]["name"];
                gram["data_status"] = gram["company"]["data_status"];
                gram["org_company_id"] = gram["company"]["_id"];
                gram["deal_stage"] = gram["company"]["deal_stage"];
                companies.push(_.merge({}, gram["company"])); // add company to collection we are going to send to our company data
                delete gram["company"];
              }
              return gram;
            });
            // do any date-parsing
            formatted = _.map(formatted, gram => {
              return _.forIn(gram, (v, k, o) => {
                if (_.isString(v)) {
                  if (vals.DATE_HEADERS.indexOf(k) >= 0) {
                    o[k] = moment.utc(v);
                  }
                }
              });
            });
            // stomp our local data
            commit("overwrite", formatted);
            commit("company/merge", { companies }, { root: true });
            // fetch addresses for the companies
            // dispatch(`address/${ADDRESS_FETCH}`, {
            //   force: true, parents: _.reduce(companies, (memo, cpny) => {
            //     memo.push(cpny._id)
            //     return memo
            //   }, [])
            // }, { root: true })

            state.loaded = true;
            return resolve(formatted);
          })
          .catch(err => {
            return reject(err);
          });
      }
    });
  },
  _apiFetchPortfolioByWorkspace(
    { state, commit, dispatch, rootGetters },
    payload
  ) {
    if (
      router &&
      router.currentRoute &&
      router.currentRoute.params.account_id
    ) {
      let url = `${router.currentRoute.params.account_id}/${payload.workspace_id}/portfolio`;
      return api
        .get(url)
        .then(res => {
          return Promise.resolve(res.data.encrypt.data);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }
  },
  _apiPostFundByWorkspace({ state, commit, dispatch, rootGetters }, payload) {
    let url = `${router.currentRoute.params.account_id}/${payload.workspace_id}/fund/company`;
    return api
      .post(url, { encrypt: { data: payload } })
      .then(res => {
        return Promise.resolve(res.data.encrypt);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  },
  apiDeletePortfolioByWorkspace(
    { state, commit, dispatch, rootGetters },
    payload
  ) {
    let url = `${router.currentRoute.params.account_id}/${payload.workspace_id}/portfolio/${payload.ucid}`;
    return api
      .delete(url)
      .then(res => {
        commit("remove", payload.ucid);
        return Promise.resolve();
      })
      .catch(err => {
        return Promise.reject({ error: err });
      });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
